<template>
  <ApplicationWrapper
    @onButtonBackClick="handleButtonBackClick"
    :headerBackTooltip="$t('overview.details_header_backToHallDetails')"
    :headerImage="hall.Image"
    :headerTitle="hall.Title">
    <template slot="details">
      <v-list two-line class="nomargin-firstchild nomargin-lastchild">
        <!-- date -->
        <Detail>
          <template slot="header">
            <DatePicker style="z-index: 1000" v-model.lazy="inputDate" :label="$t('common.label_date')" :prependIcon="'mdi-calendar'" :textFieldIsReadonly="true" />
          </template>
        </Detail>

        <!-- booking from, to and duration -->
        <Detail>
          <template slot="header">
            <v-row no-gutters align="center" style="gap: 16px" class="flex-column flex-sm-row">
              <v-col>
                <!-- time-picker from -->
                <TimePicker
                  v-model="inputTimeFrom"
                  :label="$t('common.label_from')"
                  :prependIcon="'mdi-clock-start'"
                  :min="timeMin"
                  :max="timeMax"
                  textFieldIsReadonly
                  :disabled="bookingData.wholeDay"
                  :step="durationStep" />
              </v-col>

              <v-col>
                <!-- time-picker to -->
                <TimePicker
                  v-model="inputTimeTo"
                  :label="$t('common.label_to')"
                  :prependIcon="'mdi-clock-end'"
                  :min="timeMin"
                  :max="timeMax"
                  textFieldIsReadonly
                  :disabled="bookingData.wholeDay"
                  :step="durationStep" />
              </v-col>

              <v-col>
                <!-- duration input -->
                <v-text-field
                  :value="bookingData.duration"
                  @input="handleDurationInput"
                  :label="$t('common.label_duration')"
                  :min="hall.details.MinimalBookingDuration == 0 ? hall.details.BookingBlock : hall.details.MinimalBookingDuration"
                  :disabled="bookingData.wholeDay"
                  :suffix="$t('common.label_minutes')"
                  :step="durationStep"
                  type="number"
                  :rules="commonRules"
                  prepend-icon="mdi-clock-outline" />
              </v-col>
            </v-row>

            <v-row no-gutters justify="start" align="center">
              <!-- errors -->
              <v-col cols="auto">
                <Detail v-if="errors.length > 0" :containerNoPadding="true" :standalone="true">
                  <template slot="header">
                    <v-list-item-title>
                      <div v-text="errors.length > 1 ? $t('common.label_errorsOccured') : $t('common.label_errorOccured')" />
                    </v-list-item-title>
                    <v-list-item-subtitle v-for="error in errors" :key="'hallMeetingError' + error" class="red--text">
                      {{ error }}
                    </v-list-item-subtitle>
                  </template>
                </Detail>
              </v-col>
              <v-spacer />
              <!-- whole day -->
              <v-col cols="auto">
                <v-switch v-model="inputWholeDay" :label="$t('common.label_meetingWholeDay')"></v-switch>
              </v-col>
            </v-row>
          </template>
        </Detail>

        <!-- meetings -->
        <Detail
          v-if="hall.meetings && hall.meetings.length > 0"
          :expansionStayOpen="true"
          :expandable="true"
          :prependIcon="''"
          :title="$t('common.label_meetingQueryHasResults', [hall.meetings.length])">
          <template slot="expansion-content">
            <v-list dense style="max-height: 340px; overflow-y: auto; width: 100%">
              <v-list-item-group v-model="listSelection" color="primary">
                <v-list-item
                  two-line
                  v-for="(meeting, index) in hall.meetings"
                  @click="handleMeetingSelection(meeting)"
                  :key="'hallMeeting_' + index">
                  <v-list-item-content>
                    <v-list-item-title><div v-html="meeting.From + spacing + '—' + spacing + meeting.To" /></v-list-item-title>
                    <v-list-item-subtitle> {{ $d(new Date(meeting.Date), 'longDate') }} </v-list-item-subtitle>
                    <div v-if="Object.keys(meeting).includes('rent')">
                      {{ $n(meeting.rent, 'currency', 'de-DE') }}
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </template>
        </Detail>

        <Detail v-else :title="$t('payment.noMeetings')" :subTitle="$t('payment.userMayAdjustSettings')" />
      </v-list>
    </template>
    <template slot="actionbuttons">
      <!-- meeting selector button -->
      <v-btn @click="handleBookingClick" :disabled="listSelection === undefined" class="ma-4" color="primary">
        <div v-text="meetingButtonText"></div>
      </v-btn>
    </template>
  </ApplicationWrapper>
</template>

<script>
// vuex
import { Mutators, GET_SESSIONDATA } from '@/store/storeMutator.js';
import { mapGetters } from 'vuex';

// mixins
import HelpersMixin from '@/mixins/helpers.js';
import Validation from '@/mixins/validationRules';

// comonents
import DatePicker from '@/components/controls/DatePicker.vue';
import TimePicker from '@/components/controls/TimePicker.vue';
import ApplicationWrapper from '@/components/controls/ApplicationWrapper.vue';
import Detail from '../controls/Detail.vue';

export default {
  name: 'HallMeetings',

  emits: ['onBackClick', 'onBookingDataInput', 'onMeetingClick', 'onBookingClick'],

  props: {
    hallId: String,
  },

  mixins: [HelpersMixin, Validation],

  components: {
    DatePicker,
    TimePicker,
    ApplicationWrapper,
    Detail,
  },

  mounted() {
    // set default values
    this.setDefaultValues();

    this.tryLoadData(); // load meetings
  },

  beforeDestroy() {
    // clear halls meetings (we only use them here)
    this.tryClearHallsMeetings();
  },

  data() {
    return {
      durationStep: 1,
      durationInputTimeoutId: null,
      listSelection: undefined,
      errors: [],
      bookingData: {
        date: new Date().toISOString(),
        timeFrom: '',
        timeTo: '',
        duration: 0,
        wholeDay: false,
        selectedMeeting: {},
      },
    };
  },

  methods: {
    // #region eventhandlers
    handleButtonBackClick() {
      this.$emit('onBackClick');
    },

    handleBookingClick() {
      this.$emit('onBookingClick', this.hall, this.bookingData);
    },

    handleMeetingSelection(selectedMeeting) {
      this.bookingData.selectedMeeting = selectedMeeting;

      if (!Object.keys(selectedMeeting).includes('rent')) this.$emit('onMeetingClick', this.hall, this.bookingData);
    },

    /** handles the input of the duration */
    handleDurationInput(duration) {
      this.bookingData.duration = duration;

      this.tryLoadData();
    },
    // #endregion

    tryClearHallsMeetings() {
      if (this.hall.meetings) {
        this.hall.meetings.length = 0;
        Mutators.updateHall(this.hall);
      }
    },

    setDefaultValues() {
      var durationStepConf = this.hall.details.BookingBlock;
      var minimalDurationConf = this.hall.details.MinimalBookingDuration;

      var durationStep = minimalDurationConf > 0 ? minimalDurationConf : durationStepConf;

      // set the next quarter hour to the start time
      var defaultStartTime = new Date(this.roundUpToQuarterHour(new Date(Date.now())));

      var defaultEndTime = this.addMinutesToDateObj(new Date(defaultStartTime), durationStep);

      this.durationStep = durationStep;
      this.bookingData.date = new Date(Date.now()).toDateString();
      this.bookingData.timeFrom = this.$d(defaultStartTime, 'time');
      this.bookingData.timeTo = this.$d(defaultEndTime, 'time');
      this.bookingData.duration = durationStep; // default value for the "Dauer"-input
    },

    roundUpToQuarterHour(date) {
      const quarterHour = 15 * 60 * 1000; // milliseconds in a quarter hour
      const roundedDate = new Date(Math.ceil(date.getTime() / quarterHour) * quarterHour);

      // check if the minutes are not a quarter hour value, if not round up to the next quarter hour
      if (roundedDate.getMinutes() % 15 !== 0) {
        roundedDate.setMinutes(Math.ceil(roundedDate.getMinutes() / 15) * 15);
      }

      return roundedDate;
    },

    /** checks all input fields and, if they are valid, loads the meetings */
    tryLoadData() {
      // clear halls meetings everytime the user changes values
      this.tryClearHallsMeetings();

      if (this.validateFields()) {
        // load meetings by emitting the onBookingDataInput-event
        this.$emit('onBookingDataInput', this.hall, this.bookingData);
        this.listSelection = undefined;
      }
    },

    validateFields() {
      var isValid = true;

      this.errors.length = 0;

      // we dont need validation for whole day meetings
      if (this.bookingData.wholeDay) {
        return true;
      }

      // check if all inputs have values
      if (this.isNoWs(this.bookingData.timeFrom)) {
        isValid = false;
      }

      if (this.isNoWs(this.bookingData.timeTo)) {
        isValid = false;
      }

      if (this.bookingData.duration == 0) {
        isValid = false;
      }

      // if one or more inputs are empty, we do not need further validation
      if (!isValid) return false;

      // check if duration has an valid value
      var firstPossible = Math.floor(this.bookingData.duration / this.hall.details.MinimalBookingDuration) * this.hall.details.MinimalBookingDuration;
      var nextPossible = Math.ceil(this.bookingData.duration / this.hall.details.MinimalBookingDuration) * this.hall.details.MinimalBookingDuration;

      if (this.bookingData.duration != 0 && this.bookingData.duration % this.hall.details.BookingBlock) {
        this.errors.push(this.$t('errors.meetingsInvalidDuration', [this.hall.details.MinimalBookingDuration, firstPossible, nextPossible]));
      }

      // build a Date-object out of the time and the currently selected date
      var selectedDate = new Date(this.bookingData.date);
      var year = selectedDate.getFullYear();
      var month = selectedDate.getMonth();
      var day = selectedDate.getDate();

      var timeFromAsDate = new Date(year, month, day, this.bookingData.timeFrom.split(':')[0], this.bookingData.timeFrom.split(':')[1]);
      var timeToAsDate = new Date(year, month, day, this.bookingData.timeTo.split(':')[0], this.bookingData.timeTo.split(':')[1]);

      var timeFromMillis = timeFromAsDate.getTime();
      var timeToMillis = timeToAsDate.getTime();
      var durationMillis = this.bookingData.duration * 60000;
      var minimalDurationMillis = this.hall.details.MinimalBookingDuration * 60000;
      var fromToSpan = timeFromMillis - timeToMillis;

      /*
        two cases:
        difference > 0 (+) --> To > From
        difference < 0 (-) --> To < From | we want this case :)

        --> duration <= differnce
      */
      if (fromToSpan > 0) {
        this.errors.push(this.$t('errors.endTimeBiggerThanStartTime'));
        isValid = false;
      }

      if (fromToSpan == 0) {
        this.errors.push(this.$t('errors.meetingsTimeIsEqual'));
        isValid = false;
      }

      if (this.bookingData.duration < this.durationStep) {
        this.errors.push(this.$t('errors.meetingsDurationToShort', [this.durationStep]));
        isValid = false;
      }

      if (fromToSpan != 0 && fromToSpan < 0 && fromToSpan + durationMillis > 0) {
        this.errors.push(this.$t('errors.meetingsDurationBiggerThanRange', [(fromToSpan + durationMillis) / 60000]));
        isValid = false;
      }

      if (minimalDurationMillis > 0) {
        let fromToSpanNego = fromToSpan * -1;
        if (fromToSpanNego < minimalDurationMillis) {
          this.errors.push(this.$t('errors.meetingsTimeLowerThanDuration', [minimalDurationMillis / 60000]));
          isValid = false;
        }
      }

      return isValid;
    },

    roundUpToNextMultiple(input) {
      const minValue = this.hall.details.MinimalBookingDuration;

      if (input < minValue) {
        return minValue;
      } else {
        const remainder = input % minValue;
        return remainder === 0 ? input : input + (minValue - remainder);
      }
    },
  },

  computed: {
    ...mapGetters({ SessionData: GET_SESSIONDATA }),

    meetingButtonText() {
      var textToReturn = '';

      if (this.listSelection === undefined) textToReturn = this.$t('overview.booking_action_book');
      else if (this.hall.meetings[this.listSelection].rent === 0.0) textToReturn = this.$t('overview.details_action_addHallBookingFree')
      else textToReturn = this.$t('overview.details_action_addHallBookingForX', [this.$n(this.hall.meetings[this.listSelection].rent, 'currency', 'de-DE')])

      return textToReturn;
    },

    /** Returns a hall object based on the hallId parameter */
    hall() {
      return this.SessionData.club.halls.filter((hall) => hall.Id == this.hallId)[0];
    },

    /** determines if meetings should be shown */
    showMeetings() {
      var hasMeetingsKey = Object.keys(this.hall).includes('meetings');
      if (!hasMeetingsKey) return false;

      return this.hall.meetings && this.hall.meetings.length > 0;
    },

    /** handles the input from the datepicker */
    inputDate: {
      get() {
        return this.bookingData.date;
      },
      set(date) {
        this.bookingData.date = date;

        this.tryLoadData();
      },
    },

    /** handles the timeFrom-Timepicker */
    inputTimeFrom: {
      get() {
        return String(this.bookingData.timeFrom);
      },
      set(timeFrom) {
        this.bookingData.timeFrom = timeFrom;
        this.tryLoadData();
      },
    },

    /** handles the timeTo-input */
    inputTimeTo: {
      get() {
        return this.bookingData.timeTo;
      },
      set(timeTo) {
        this.bookingData.timeTo = timeTo;
        this.tryLoadData();
      },
    },

    // #region time picker
    timeMin() {
      return this.hallOpeningTime;
    },

    timeMax() {
      var closingTime = new Date(this.hall.details.OpeningHours[new Date(Date.now()).getDay()].To);
      
      return this.$d(closingTime, 'time');
    },

    // #endregion
    /** handles the whole-day switch change */
    inputWholeDay: {
      get() {
        return this.bookingData.wholeDay;
      },
      set(wholeDay) {
        this.bookingData.wholeDay = wholeDay;
        this.tryLoadData();
      },
    },

    /** returns the opening time of the current day for the hall */
    hallOpeningTime() {
      return this.$d(new Date(this.hall.details.OpeningHours[new Date(Date.now()).getDay()].From), 'time');
    },

    /** returns the closing time of the current day for the hall */
    hallClosingTime() {
      return this.$d(new Date(this.hall.details.OpeningHours[new Date(Date.now()).getDay()].To), 'time');
    },
  },
};
</script>

<style></style>
